import { toast } from 'react-hot-toast';
import { UNAUTHORIZED } from './constants';
import { BACKEND_URL } from '../config/env';
import { v4 as uuidv4 } from "uuid";

let hasShownSessionExpiredToast = false;

export const getDeviceId = (): string => {
  // Guard for SSR
  if (typeof window === "undefined") return "server";

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem("device_id", deviceId);
  }
  return deviceId;
};

export const apiClient = async (
    url: string, 
    options: RequestInit & { isErrorToastSuppressed?: boolean; isErrorHandlingSkipped?: boolean } = {}
): Promise<Response> => {

    // Don't use Authorization header — rely on browser cookies (access_token)
    const { isErrorToastSuppressed, isErrorHandlingSkipped, ...fetchOptions } = options;
    const headers: Record<string, string> = {
        ...(getDeviceId() ? { 'Device-ID': getDeviceId() } : {}),
    };

    // If FormData is being used, don't manually set Content-Type
    if (!(fetchOptions.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    const updatedOptions: RequestInit = {
        ...fetchOptions,
        headers: {
            ...headers,
            ...fetchOptions.headers,
        },
        credentials: 'include', // 🔥 REQUIRED: allows cookies to be sent
    };

    try {
        const response = await fetch(`${BACKEND_URL}${url}`, updatedOptions);

        if (isErrorHandlingSkipped) {
            return response;
        }

        if (!response.ok) {
            const rawText = await response.text();
            let detailFromBody: string | null = null;
            try {
                const parsedBody = JSON.parse(rawText);
                // Handle both string and array detail (Pydantic validation errors)
                if (parsedBody.detail) {
                    if (Array.isArray(parsedBody.detail)) {
                        // Format validation errors: "field: message"
                        detailFromBody = parsedBody.detail
                            .map((err: { loc?: unknown[]; msg?: string }) => {
                                const field = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : 'field';
                                return `${field}: ${err.msg || 'Invalid value'}`;
                            })
                            .join('; ');
                    } else if (typeof parsedBody.detail === 'string') {
                        detailFromBody = parsedBody.detail;
                    } else {
                        detailFromBody = String(parsedBody.detail);
                    }
                } else if (parsedBody.message) {
                    detailFromBody = typeof parsedBody.message === 'string' 
                        ? parsedBody.message 
                        : String(parsedBody.message);
                }
            } catch {
                // Non-JSON response body; use raw text if present for thrown error below.
            }

            // Determine toast message and thrown error message separately
            let toastMessage: string = 'An unexpected error occurred.';
            let thrownMessage: string = `Error: ${response.statusText}`;

            if (detailFromBody) {
                toastMessage = detailFromBody;
                thrownMessage = detailFromBody;
            } else if (response.status === 502) {
                // Special-case upstream gateway timeouts
                toastMessage = '502 Gateway Timeout';
                thrownMessage = response.statusText || 'Bad Gateway';
            } else if (rawText) {
                // Non-JSON body with text: throw the raw text, but show generic toast
                thrownMessage = rawText;
            }

            // If the user is not authenticated, notify the app (AuthProvider will handle redirect/reset)
            if (response.status === 401) {
                try {
                    // Only notify app if we previously had a logged-in session.
                    // Prevents dispatching on initial /auth/me checks / landing on login.
                    const wasLoggedIn = Boolean(window.__IS_MSAPP_LOGGED_IN__);
                    if (wasLoggedIn) {
                      window.dispatchEvent(new CustomEvent('msapp:unauthenticated'));
                      if (!hasShownSessionExpiredToast) {
                        hasShownSessionExpiredToast = true;
                        toast.error("Session expired. Please log in.", {
                          duration: 7000,
                          id: 'session-expired',
                        });
                      }
                    }
                } catch {
                    void 0;
                }
                // Don't show the generic 401 toast here; preserve backend detail for callers.
                throw new Error(detailFromBody || 'Unauthorized');
            }

            // Skip error toasts after logout / session clear,  
            // so that late responses don't show on the logout/login screen
            const isLoggedInSession = Boolean(window.__IS_MSAPP_LOGGED_IN__);
            if (!isErrorToastSuppressed && isLoggedInSession) {
                toast.error(toastMessage, { duration: 7000 });
            }
            throw new Error(thrownMessage);
        }

        return response;
    } catch (error: unknown) {
        throw error;
    }
};

export const throwUnauthorized = () => {
    const error = new Error('Unauthorized access');
    error.name = UNAUTHORIZED;
    throw error;
};

// Test hook: allows unit tests to reset module-level toast state.
export const __resetApiClientToastStateForTests = () => {
    hasShownSessionExpiredToast = false;
};

export const enforceLoggedIn = (req: Request, user: unknown) => {
    if (!user) throwUnauthorized();
    return user;
};
